import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { UserActivityService } from './user-activity.service';
import { ColDef, GridApi, GridReadyEvent, ColumnApi } from 'ag-grid-community';
import { NgbAccordion } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from 'src/app/_services/alert.service';
import { ExportTrackingService } from 'src/app/_services/export-tracking.service';

@Component({
  selector: 'app-user-activity',
  templateUrl: './user-activity.component.html',
  styleUrls: ['./user-activity.component.scss']
})
export class UserActivityComponent implements OnInit {
  // Form for filters
  filterForm: FormGroup;
  
  // Grid properties
  private gridApi!: GridApi;
  private gridColumnApi!: ColumnApi;
  public rowData: any[] = [];
  public defaultColDef: ColDef = {
    flex: 1,
    minWidth: 150,
    filter: false,
    resizable: true,
    sortable: true
  };

  // Pagination properties
  pageSize: number = 10;
  pageSizeOptions: number[] = [5, 10, 20, 50];
  currentPage: number = 0;
  collectionSize: number = 0;
  
  // Filter options
  users: any[] = [];
  categories: any[] = [];
  methods: string[] = ['VIEW', 'ADD', 'UPDATE', 'DELETE', 'DOWNLOAD', 'LOGIN', 'LOGOUT'];
  methodsMap: { [key: string]: string } = {
    'VIEW': 'View',
    'ADD': 'Add', 
    'UPDATE': 'Update',
    'DELETE': 'Delete',
    'MODIFY': 'Modify',
    'UNMASK': 'Unmask',
    'UNMASK_FIELD': 'Unmask Field',
    'UNMASK_CELL': 'Unmask Cell',
    'CHECK': 'Check',
    'DOWNLOAD': 'Download',
    'LOGIN': 'Login',
    'LOGOUT': 'Logout',
    'AUTHENTICATE': 'Authenticate',
    // Legacy support for old method names
    'GET': 'View',
    'POST': 'Add',
    'PUT': 'Update',
    'PATCH': 'Modify'
  };
  loading: boolean = false;

  // Column definitions for ag-grid with enhanced menu hierarchy fields
  public columnDefs: ColDef[] = [
    { field: 'timestamp', headerName: 'Timestamp', sortable: true, minWidth: 180, valueFormatter: (params) => {
      return params.value ? new Date(params.value).toLocaleString() : '';
    }},
    { field: 'user', headerName: 'User Email', sortable: true, minWidth: 120 },
    { field: 'method', headerName: 'Method', sortable: true, minWidth: 100, valueFormatter: (params) => {
      // First try to get the display name from methodsMap, fallback to the raw value
      return this.methodsMap[params.value] || params.value || 'Unknown';
    }},
    { field: 'mainMenu', headerName: 'Main Menu', sortable: true, minWidth: 150 },
    { field: 'subMenu', headerName: 'Sub Menu', sortable: true, minWidth: 150 },
    { field: 'tabMenu', headerName: 'Tab/Section', sortable: true, minWidth: 150 },
    { field: 'wizardStepName', headerName: 'Step Name', sortable: true, minWidth: 150, valueFormatter: (params) => {
      if (params.value) {
        const stepNumber = params.data.wizardStepNumber;
        return stepNumber ? `${params.value} (Step ${stepNumber})` : params.value;
      }
      return '';
    }},
    { field: 'menuBreadcrumb', headerName: 'Menu Path', sortable: true, minWidth: 250, tooltipField: 'menuBreadcrumb' },
    { field: 'category', headerName: 'Category', sortable: true, minWidth: 150 },
    { field: 'action', headerName: 'Action', sortable: true, minWidth: 150 },
    { field: 'url', headerName: 'API URL', sortable: true, minWidth: 200, tooltipField: 'url' },
    // { field: 'sessionDuration', headerName: 'Session Duration', sortable: true, minWidth: 140, valueFormatter: (params) => {
    //   return params.value ? this.formatDuration(params.value) : '';
    // }},
    { 
      field: 'payload', 
      headerName: 'Details', 
      sortable: false,
      minWidth: 100,
      cellRenderer: (params: any) => {
        return '<button class="btn bg-danger-custom btn-sm"><i class="feather icon-eye"></i> View</button>';
      },
      onCellClicked: (params: any) => {
        this.viewPayloadDetails(params.data);
      }
    }
  ];

  // Column selector options
  public availableColumns: { field: string, displayName: string, selected: boolean }[] = [
    { field: 'timestamp', displayName: 'Timestamp', selected: true },
    { field: 'user', displayName: 'User', selected: true },
    { field: 'method', displayName: 'Method', selected: true },
    { field: 'mainMenu', displayName: 'Main Menu', selected: true },
    { field: 'subMenu', displayName: 'Sub Menu', selected: true },
    { field: 'tabMenu', displayName: 'Tab/Section', selected: true },
    // { field: 'wizardStepName', displayName: 'Step Name', selected: true },
    { field: 'menuBreadcrumb', displayName: 'Menu Path', selected: false },
    { field: 'category', displayName: 'Category', selected: false },
    { field: 'action', displayName: 'Action', selected: false },
    { field: 'url', displayName: 'API URL', selected: false },
    { field: 'sessionDuration', displayName: 'Session Duration', selected: true },
    { field: 'payload', displayName: 'Details', selected: true }
  ];
  
  // Selected activity for payload modal
  selectedActivity: any = null;
  showPayloadModal: boolean = false;

  @ViewChild('acc') private acc!: NgbAccordion;

  constructor(
    private formBuilder: FormBuilder,
    private userActivityService: UserActivityService,
    private alertService: AlertService,
    private exportTrackingService: ExportTrackingService
  ) {
    // Initialize filter form
    this.filterForm = this.formBuilder.group({
      user: [''],
      category: [''],
      method: [''],
      startDate: [''],
      endDate: ['']
    });
  }

  ngOnInit(): void {
    // Load filter options
    this.loadUsers();
    this.loadCategories();
    // Apply default column visibility
    this.updateColumnVisibility();
  }

  /**
   * Update column visibility based on selection
   */
  updateColumnVisibility(): void {
    const visibleColumns = this.availableColumns
      .filter(col => col.selected)
      .map(col => this.columnDefs.find(def => def.field === col.field))
      .filter(col => col !== undefined);
    
    if (this.gridColumnApi) {
      this.gridColumnApi.setColumnsVisible(
        this.columnDefs.map(col => col.field!),
        false
      );
      this.gridColumnApi.setColumnsVisible(
        visibleColumns.map(col => col?.field!),
        true
      );
    }
  }

  /**
   * Toggle column visibility
   */
  toggleColumn(column: { field: string, displayName: string, selected: boolean }): void {
    column.selected = !column.selected;
    this.updateColumnVisibility();
  }

  /**
   * Format milliseconds to readable duration
   */
  formatDuration(ms: number): string {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    return hours > 0 
      ? `${hours}h ${minutes % 60}m ${seconds % 60}s` 
      : minutes > 0 
        ? `${minutes}m ${seconds % 60}s` 
        : `${seconds}s`;
  }

  /**
   * Load users for filter dropdown
   */
  loadUsers(): void {
    this.userActivityService.getActivityUsers().subscribe({
      next: (data: any) => {
        if (data && Array.isArray(data)) {
          this.users = data;
        } else {
          this.users = [];
        }
      },
      error: (err) => {
        this.users = [];
        this.alertService.toastErrorMessageAlert({
          title: err?.message || 'Failed to load users',
        });
      }
    });
  }

  /**
   * Load categories for filter dropdown
   */
  loadCategories(): void {
    this.userActivityService.getActivityCategories().subscribe({
      next: (response: any) => {
        if (response && response.data && Array.isArray(response.data)) {
          this.categories = response.data;
        } else {
          this.categories = [];
          this.alertService.toastWarringMessageAlert({
            title: 'Failed to load categories',
          });
        }
      },
      error: (err) => {
        this.categories = [];
        this.alertService.toastErrorMessageAlert({
          title: err?.message || 'Failed to load categories',
        });
      }
    });
  }

  /**
   * Handle grid ready event
   */
  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.gridApi.showLoadingOverlay();
    this.updateColumnVisibility();
    this.loadActivityLogs();
  }

  /**
   * Load activity logs with current filters and pagination
   */
  loadActivityLogs(): void {
    this.loading = true;
    const filters = this.filterForm.value;
    
    console.log('Loading activity logs with:', {
      pageSize: this.pageSize,
      currentPage: this.currentPage,
      filters: filters
    });
    
    this.userActivityService.getUserActivityLogs(filters, this.pageSize, this.currentPage).subscribe({
      next: (data: any) => {
        this.loading = false;
        if (data) {
          this.rowData = data.content || [];
          this.collectionSize = data.totalElements || 0;
          this.gridApi?.hideOverlay();
        } else {
          this.rowData = [];
          this.collectionSize = 0;
          this.gridApi?.hideOverlay();
          this.alertService.toastWarringMessageAlert({
            title: 'No activity logs found',
          });
        }
      },
      error: (err) => {
        this.loading = false;
        this.rowData = [];
        this.collectionSize = 0;
        this.gridApi?.hideOverlay();
        this.alertService.toastErrorMessageAlert({
          title: err?.message || 'Failed to load activity logs',
        });
      }
    });
  }

  /**
   * Handle page change event
   */
  onPageChange(page: number): void {
    console.log('Page change clicked:', page);
    console.log('Current page before:', this.currentPage);
    this.currentPage = page - 1; // Convert to 0-based index
    console.log('Current page after:', this.currentPage);
    console.log('Page size:', this.pageSize);
    this.loadActivityLogs();
  }

  /**
   * Handle page size change event
   */
  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 0; // Reset to first page
    this.loadActivityLogs();
  }

  /**
   * Apply filters from form
   */
  applyFilters(): void {
    this.currentPage = 0; // Reset to first page
    this.loadActivityLogs();
  }

  /**
   * Reset filters
   */
  resetFilters(): void {
    this.filterForm.reset();
    this.currentPage = 0;
    this.loadActivityLogs();
  }

  /**
   * Close payload modal
   */
  closePayloadModal(): void {
    this.showPayloadModal = false;
    this.selectedActivity = null;
  }

  /**
   * View payload details in modal
   */
  viewPayloadDetails(activity: any): void {
    this.selectedActivity = activity;
    this.showPayloadModal = true;
  }

  /**
   * Export to CSV
   */
  exportToCSV(): void {
    if (!this.rowData || this.rowData.length === 0) {
      this.alertService.toastWarringMessageAlert({
        title: 'No data to export',
      });
      return;
    }

    const fileName = `user_activity_${new Date().toISOString().split('T')[0]}.csv`;
    
    // First, make a mock HTTP call to track this download action in audit logs
    this.exportTrackingService.trackCSVExport(
      fileName,
      this.rowData.length,
      'USER_ACTIVITY_REPORT',
      this.filterForm.value
    ).subscribe({
      next: (response) => {
        console.log('Export download tracked in audit logs:', response);
        // Proceed with actual CSV export after audit tracking
        this.performCSVExport(fileName);
      },
      error: (error) => {
        console.log('Export tracking completed (audit logged):', error);
        // Even if tracking fails, proceed with export since audit was logged by interceptor
        this.performCSVExport(fileName);
      }
    });
  }

  /**
   * Perform the actual CSV export after audit tracking
   */
  private performCSVExport(fileName: string): void {
    // Prepare CSV data
    const headers = this.availableColumns
      .filter(col => col.selected && col.field !== 'payload')
      .map(col => col.displayName);
    
    const rows = this.rowData.map(row => {
      return this.availableColumns
        .filter(col => col.selected && col.field !== 'payload')
        .map(col => {
          const field = col.field;
          if (field === 'timestamp' && row[field]) {
            return new Date(row[field]).toLocaleString();
          } else if (field === 'method') {
            return this.methodsMap[row[field]] || row[field] || '';
          } else if (field === 'sessionDuration' && row[field]) {
            return this.formatDuration(row[field]);
          } else if (field === 'wizardStepName' && row[field]) {
            const stepNumber = row.wizardStepNumber;
            return stepNumber ? `${row[field]} (Step ${stepNumber})` : row[field];
          }
          return row[field] || '';
        });
    });

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
  }

  /**
   * Export to Excel with audit tracking
   */
  exportToExcel(): void {
    if (!this.rowData || this.rowData.length === 0) {
      this.alertService.toastWarringMessageAlert({
        title: 'No data to export',
      });
      return;
    }

    const fileName = `user_activity_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    // First, make a mock HTTP call to track this download action in audit logs
    this.exportTrackingService.trackExcelExport(
      fileName,
      this.rowData.length,
      'USER_ACTIVITY_REPORT',
      this.filterForm.value
    ).subscribe({
      next: (response) => {
        console.log('Excel export download tracked in audit logs:', response);
        // Proceed with actual Excel export after audit tracking
        this.performExcelExport(fileName);
      },
      error: (error) => {
        console.log('Excel export tracking completed (audit logged):', error);
        // Even if tracking fails, proceed with export since audit was logged by interceptor
        this.performExcelExport(fileName);
      }
    });
  }

  /**
   * Perform the actual Excel export after audit tracking
   */
  private performExcelExport(fileName: string): void {
    // Import XLSX dynamically to avoid bundle size issues
    import('xlsx').then(XLSX => {
      try {
        // Get exportable columns (exclude payload and actions)
        const exportableColumns = this.availableColumns.filter(col => 
          col.selected && col.field !== 'payload'
        );

        // Prepare data for Excel export
        const excelData = this.rowData.map(row => {
          const exportRow: any = {};
          exportableColumns.forEach(col => {
            const headerName = col.displayName;
            const field = col.field;
            
            if (field === 'timestamp' && row[field]) {
              exportRow[headerName] = new Date(row[field]).toLocaleString();
            } else if (field === 'method') {
              exportRow[headerName] = this.methodsMap[row[field]] || row[field] || '';
            } else if (field === 'sessionDuration' && row[field]) {
              exportRow[headerName] = this.formatDuration(row[field]);
            } else if (field === 'wizardStepName' && row[field]) {
              const stepNumber = row.wizardStepNumber;
              exportRow[headerName] = stepNumber ? `${row[field]} (Step ${stepNumber})` : row[field];
            } else {
              exportRow[headerName] = row[field] || '';
            }
          });
          return exportRow;
        });

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        
        // Set column widths
        const columnWidths = exportableColumns.map(col => ({
          wch: Math.max(15, (col.displayName || '').length + 5)
        }));
        worksheet['!cols'] = columnWidths;

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'User Activity Report');

        // Generate Excel file and save
        const excelBuffer = XLSX.write(workbook, { 
          bookType: 'xlsx', 
          type: 'array',
          compression: true
        });
        
        // Create blob and download
        const blob = new Blob([excelBuffer], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' 
        });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        
      } catch (error) {
        console.error('Error exporting to Excel:', error);
        this.alertService.toastErrorMessageAlert({
          title: 'Failed to export data to Excel. Please try again.',
        });
      }
    }).catch(error => {
      console.error('Error loading XLSX library:', error);
      this.alertService.toastErrorMessageAlert({
        title: 'Failed to load Excel export library. Please try again.',
      });
    });
  }
}
